import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getAllProcessKPIs,
  createProcessKPI,
  updateProcessKPI,
  deleteProcessKPI,
  getAllKPIs,
} from "../services/kpiService";
import { getProcesses } from "../services/processService";
import { useAppContext } from "../context/AppContext";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";

const ProcessKPIPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  // State
  const [processKPIs, setProcessKPIs] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [processFilter, setProcessFilter] = useState("All");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [selectedProcessKPI, setSelectedProcessKPI] = useState(null);
  const [formData, setFormData] = useState({
    process_id: "",
    kpi_id: "",
    value: "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processKPIToDelete, setProcessKPIToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [processKPIData, kpiData, processData] = await Promise.all([
        getAllProcessKPIs(),
        getAllKPIs(),
        getProcesses(),
      ]);
      setProcessKPIs(Array.isArray(processKPIData) ? processKPIData : []);
      setKpis(Array.isArray(kpiData) ? kpiData : []);
      setProcesses(Array.isArray(processData) ? processData : []);
    } catch (err) {
      console.error("Failed to load Process KPIs:", err);
      setError(err.message || "Failed to load Process KPIs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get process name by ID
  const getProcessName = useCallback(
    (processId) => {
      const process = processes.find(
        (p) => p.process_id === processId || p.id === processId,
      );
      return process?.process_name || process?.name || `Process #${processId}`;
    },
    [processes],
  );

  // Get KPI name by ID
  const getKPIName = useCallback(
    (kpiId) => {
      const kpi = kpis.find((k) => k.id === kpiId);
      return kpi?.kpiName || `KPI #${kpiId}`;
    },
    [kpis],
  );

  // Get KPI UoM
  const getKPIUoM = (kpiId) => {
    const kpi = kpis.find((k) => k.id === kpiId);
    return kpi?.kpi_uom?.uomValue || "";
  };

  // Filtered Process KPIs
  const filteredProcessKPIs = useMemo(() => {
    return processKPIs.filter((pk) => {
      const processName =
        pk.process?.process_name || getProcessName(pk.process_id);
      const kpiName = pk.kpi?.kpiName || getKPIName(pk.kpi_id);

      const matchesSearch =
        processName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        kpiName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pk.value || "").toLowerCase().includes(searchTerm.toLowerCase());

      const matchesProcess =
        processFilter === "All" || String(pk.process_id) === processFilter;

      return matchesSearch && matchesProcess;
    });
  }, [processKPIs, searchTerm, processFilter, getKPIName, getProcessName]);

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      process_id:
        processes.length > 0 ? processes[0].process_id || processes[0].id : "",
      kpi_id: kpis.length > 0 ? kpis[0].id : "",
      value: "",
    });
    setFormError(null);
    setModalMode("create");
    setSelectedProcessKPI(null);
    setShowModal(true);
  };

  const openEditModal = (pk) => {
    setFormData({
      process_id: pk.process_id || "",
      kpi_id: pk.kpi_id || "",
      value: pk.value || "",
    });
    setFormError(null);
    setModalMode("edit");
    setSelectedProcessKPI(pk);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProcessKPI(null);
    setFormError(null);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.process_id) {
      setFormError("Process is required");
      return;
    }
    if (!formData.kpi_id) {
      setFormError("KPI is required");
      return;
    }
    if (!formData.value.trim()) {
      setFormError("Value is required");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (modalMode === "create") {
        await createProcessKPI(formData);
      } else if (modalMode === "edit" && selectedProcessKPI) {
        await updateProcessKPI(selectedProcessKPI.id, {
          value: formData.value,
        });
      }
      await loadData();
      closeModal();
    } catch (err) {
      console.error("Failed to save Process KPI:", err);
      setFormError(err.message || "Failed to save Process KPI");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteConfirm = (pk) => {
    setProcessKPIToDelete(pk);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setProcessKPIToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleDelete = async () => {
    if (!processKPIToDelete) return;
    setDeleting(true);
    try {
      await deleteProcessKPI(processKPIToDelete.id);
      await loadData();
      closeDeleteConfirm();
    } catch (err) {
      console.error("Failed to delete Process KPI:", err);
      alert(err.message || "Failed to delete Process KPI");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading Process KPIs...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button className="retry-btn" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <button
              className="back-link"
              onClick={() => setActiveSection("kpi-management")}
            >
              ← Back to KPI Management
            </button>
            <h2 className="page-title">Process KPIs</h2>
            <p className="page-subtitle">
              Link KPIs to processes and track their values
            </p>
          </div>
          <button className="create-btn" onClick={openCreateModal}>
            + Link KPI to Process
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by process, KPI, or value..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filters">
            <select
              value={processFilter}
              onChange={(e) => setProcessFilter(e.target.value)}
              className="filter-select"
            >
              <option value="All">All Processes</option>
              {processes.map((p) => (
                <option key={p.process_id || p.id} value={p.process_id || p.id}>
                  {p.process_name || p.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Process KPI Table */}
        <div className="kpi-table-container">
          {filteredProcessKPIs.length === 0 ?
            <div className="no-results">
              <p>No Process KPIs found.</p>
              {processKPIs.length === 0 && (
                <button className="create-btn" onClick={openCreateModal}>
                  Link your first KPI to a Process
                </button>
              )}
            </div>
          : <table className="kpi-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Process</th>
                  <th>KPI</th>
                  <th>Value</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProcessKPIs.map((pk) => (
                  <tr key={pk.id}>
                    <td>{pk.id}</td>
                    <td className="process-name-cell">
                      {pk.process?.process_name ||
                        getProcessName(pk.process_id)}
                    </td>
                    <td className="kpi-name-cell">
                      {pk.kpi?.kpiName || getKPIName(pk.kpi_id)}
                    </td>
                    <td>
                      <span className="value-badge">
                        {pk.value}
                        {pk.kpi?.kpi_uom?.uomValue && (
                          <span className="uom-suffix">
                            {" "}
                            {pk.kpi.kpi_uom.uomValue}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{formatDate(pk.created_at)}</td>
                    <td>{formatDate(pk.updated_at)}</td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => openEditModal(pk)}
                        title="Edit Value"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => openDeleteConfirm(pk)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === "create" ?
                  "Link KPI to Process"
                : "Edit Process KPI Value"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {formError && <div className="form-error">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="process_id">Process *</label>
                  <select
                    id="process_id"
                    name="process_id"
                    value={formData.process_id}
                    onChange={handleInputChange}
                    required
                    disabled={modalMode === "edit"}
                  >
                    <option value="">Select Process</option>
                    {processes.map((p) => (
                      <option
                        key={p.process_id || p.id}
                        value={p.process_id || p.id}
                      >
                        {p.process_name || p.name}
                      </option>
                    ))}
                  </select>
                  {processes.length === 0 && (
                    <p className="form-hint">No processes available.</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="kpi_id">KPI *</label>
                  <select
                    id="kpi_id"
                    name="kpi_id"
                    value={formData.kpi_id}
                    onChange={handleInputChange}
                    required
                    disabled={modalMode === "edit"}
                  >
                    <option value="">Select KPI</option>
                    {kpis.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.kpiName}
                        {k.kpi_uom?.uomValue && ` (${k.kpi_uom.uomValue})`}
                      </option>
                    ))}
                  </select>
                  {kpis.length === 0 && (
                    <p className="form-hint">
                      No KPIs available.{" "}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => setActiveSection("kpi-management")}
                      >
                        Create one first
                      </button>
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="value">
                    Value *{" "}
                    {formData.kpi_id && getKPIUoM(Number(formData.kpi_id)) && (
                      <span className="uom-hint">
                        ({getKPIUoM(Number(formData.kpi_id))})
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="value"
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="Enter value (e.g., 85.5)"
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={saving}
                  >
                    {saving ?
                      "Saving..."
                    : modalMode === "create" ?
                      "Link KPI"
                    : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={closeDeleteConfirm}>
          <div
            className="modal-content modal-small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Remove Process KPI Link</h3>
              <button className="modal-close" onClick={closeDeleteConfirm}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to remove the KPI link for{" "}
                <strong>
                  {processKPIToDelete?.kpi?.kpiName ||
                    getKPIName(processKPIToDelete?.kpi_id)}
                </strong>{" "}
                from{" "}
                <strong>
                  {processKPIToDelete?.process?.process_name ||
                    getProcessName(processKPIToDelete?.process_id)}
                </strong>
                ?
              </p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeDeleteConfirm}>
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessKPIPage;
